import { motion } from "framer-motion";
import { Fuel, Users, Gift, ArrowRight } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export const ReferralSection = () => {
  return (
    <section id="referrals" className="py-24 md:py-32 bg-[#050505]" data-testid="referral-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div {...fadeUp} className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Mates Rates</span>
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-6">
            Free Fuel.<br />
            <span className="text-[#3b82f6]">Every Referral.</span>
          </h2>
          <p className="text-zinc-400 font-body text-base md:text-lg max-w-2xl mx-auto">
            We know fuel's cooked right now. So when you send a mate our way and they get their car done, 
            we'll shout you a free tank of fuel. No catch, no limits.
          </p>
        </motion.div>

        {/* How it works */}
        <motion.div 
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16"
        >
          {/* Step 1 */}
          <div className="bg-[#0a0a0a] border border-white/5 p-8 relative group hover:border-[#3b82f6]/30 transition-colors">
            <div className="absolute top-6 right-6 text-[#3b82f6]/20 font-heading text-6xl">1</div>
            <div className="w-14 h-14 bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center mb-6">
              <Gift size={24} className="text-[#3b82f6]" />
            </div>
            <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-3">
              Get Your Code
            </h3>
            <p className="text-zinc-500 font-body text-sm leading-relaxed">
              Once we finish your car, you'll get a personal referral code. It's yours to share with anyone.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-[#0a0a0a] border border-white/5 p-8 relative group hover:border-[#3b82f6]/30 transition-colors">
            <div className="absolute top-6 right-6 text-[#3b82f6]/20 font-heading text-6xl">2</div>
            <div className="w-14 h-14 bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center mb-6">
              <Users size={24} className="text-[#3b82f6]" />
            </div>
            <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-3">
              Send Your Mates
            </h3>
            <p className="text-zinc-500 font-body text-sm leading-relaxed">
              When they book in and mention your code (or use it online), we'll link it to you.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-[#0a0a0a] border border-white/5 p-8 relative group hover:border-[#3b82f6]/30 transition-colors">
            <div className="absolute top-6 right-6 text-[#3b82f6]/20 font-heading text-6xl">3</div>
            <div className="w-14 h-14 bg-[#22c55e]/10 border border-[#22c55e]/20 flex items-center justify-center mb-6">
              <Fuel size={24} className="text-[#22c55e]" />
            </div>
            <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-3">
              Free Tank of Fuel
            </h3>
            <p className="text-zinc-500 font-body text-sm leading-relaxed">
              Once their job's done and they've picked up, we'll hit you up. Free fuel, sorted.
            </p>
          </div>
        </motion.div>

        {/* Bottom highlight */}
        <motion.div 
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-gradient-to-r from-[#3b82f6]/10 via-[#3b82f6]/5 to-transparent border border-[#3b82f6]/20 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-6">
            <div className="hidden sm:flex w-16 h-16 bg-[#22c55e] items-center justify-center flex-shrink-0">
              <Fuel size={28} className="text-black" />
            </div>
            <div>
              <h3 className="font-heading text-xl md:text-2xl uppercase tracking-wide text-white mb-1">
                Unlimited Referrals
              </h3>
              <p className="text-zinc-400 font-body text-sm md:text-base">
                No cap on how many mates you send. Every referral = another free tank. Stack 'em up.
              </p>
            </div>
          </div>
          
          <div className="flex-shrink-0">
            <a
              href="#quote"
              className="group flex items-center gap-3 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-[#3b82f6]/80 transition-colors"
            >
              Get Started
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </motion.div>

        {/* Fine print */}
        <motion.p 
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center text-zinc-600 text-xs font-body mt-8"
        >
          Standard passenger vehicles only. We'll match the fuel to your car - no aircraft carriers or mining trucks, sorry legends.
        </motion.p>
      </div>
    </section>
  );
};

export default ReferralSection;
